import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  TextField,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import PeopleIcon from "@mui/icons-material/People";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ReceiptIcon from "@mui/icons-material/Receipt";
import WSTextField, {
  CurrencyType,
  TextType,
} from "../../Components/TextBox/WSTextBox";
import { useFieldArray, useForm } from "react-hook-form";
import WSButton from "../../Components/Button/WSButton";
import { WSIcons } from "../../commonFunctions/iIcons";
import WSSelectField from "../../Components/Select/WSSelect";
import { WSView } from "../../Components/View/WSView";

const InvoicePage: React.FC = () => {
  const { register, control, watch, getValues, setValue } = useForm();

  const { fields, append, remove, replace } = useFieldArray({
    name: "items",
    control,
  });

  const [subtotal, setSubtotal] = useState(0);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    calculateTotal(subtotal);
  }, [subtotal, watch("discount"), watch("adjustments")]);

  const calculateTotal = (newSubtotal: number) => {
    const discount = parseFloat(watch("discount")) || 0;
    const adjustments = parseFloat(watch("adjustments")) || 0;
    const newTotal = newSubtotal - discount + adjustments;
    setTotal(newTotal);
  };
  const recalculateSubtotal = () => {
    const updatedItems = getValues("items") || [];
    let newSubtotal = updatedItems.reduce((sum: any, item: any) => {
      const quantity = parseFloat(item.quantity) || 1;
      const unitPrice = parseFloat(item.unitPrice) || 0;
      return sum + quantity * unitPrice;
    }, 0);

    setSubtotal(newSubtotal);
    calculateTotal(newSubtotal);
  };

  const handleQuantityChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setValue(`items.${index}.quantity`, event.target.value);

    setValue(`items.${index}.total`, total.toString()); // Convert to string

    // Recalculate Subtotal
    recalculateSubtotal();
  };

  const handleUnitPriceChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setValue(`items.${index}.unitPrice`, event.target.value);
    setValue(`items.${index}.total`, total.toString()); // Convert to string

    // Recalculate Subtotal
    recalculateSubtotal();
  };

  // Add this above return inside component
  const isFormValid = () => {
    const customerName = watch("customerName");
    const items = watch("items") || [];

    if (!customerName) return false;

    if (
      items.length === 0 ||
      !items.some(
        (item: any) =>
          item.itemName &&
          Number(item.quantity) > 0 &&
          Number(item.unitPrice) > 0
      )
    ) {
      return false;
    }

    if (total <= 0) return false;

    return true;
  };

  return (
    <Grid p={3}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Paper elevation={0} sx={{ p: 3, minHeight: "100vh" }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: "bold" }}>
                  Enter details to make your first Sale 🚀
                </Typography>
                <Typography variant="caption" sx={{ mb: 2 }}>
                  First sale is made in less than a minute on vyapar
                </Typography>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">Invoice Details :</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption">Invoice No:1</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption">
                  Invoice Date:{new Date().toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">Bill To :</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <WSTextField
                  label={"Customer Name"}
                  {...register("customerName")}
                  id="customerName"
                  name="customerName"
                  control={control}
                  rules={{ required: false }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    borderStyle: "dotted",
                    borderColor: "#90caf9",
                    borderWidth: "2px",
                    textTransform: "none",
                    color: "#1976d2",
                    py: 2,
                    my: 2,
                  }}
                  onClick={() =>
                    append({
                      itemId: "",
                      quantity: "",
                      unitPrice: "",
                      amount: "",
                      total: "",
                    })
                  }
                >
                  {" "}
                  + Add Item
                </Button>
              </Grid>
              {/* Dynamic Item Fields */}

              {fields.map((row, index) => (
                <Grid
                  container
                  spacing={2}
                  key={row.id}
                  alignItems="center"
                  mt={1}
                  ml={1}
                >
                  <Grid item xs={2}>
                    <WSTextField
                      label="Item Name"
                      {...register(`items.${index}.itemName`)}
                      id={`itemName-${index}`}
                      control={control}
                    />
                  </Grid>

                  <Grid item xs={1.5}>
                    <WSTextField
                      label="Unit"
                      {...register(`items.${index}.unit`)}
                      id={`unit-${index}`}
                      control={control}
                    />
                  </Grid>

                  <Grid item xs={2}>
                    <WSTextField
                      label="Unit Price"
                      {...register(`items.${index}.unitPrice`, {
                        onChange: (event) =>
                          handleUnitPriceChange(index, event),
                      })}
                      id={`unitPrice-${index}`}
                      control={control}
                      type={TextType.Currency}
                      currency={CurrencyType.Rupee}
                    />
                  </Grid>

                  <Grid item xs={2}>
                    <WSTextField
                      id={`quantity-${index}`}
                      label="Quantity"
                      {...register(`items.${index}.quantity`, {
                        onChange: (event) => handleQuantityChange(index, event),
                      })}
                      control={control}
                      type={TextType.Number}
                    />
                  </Grid>

                  {/* Total Amount for each product*/}
                  <Grid item xs={1}>
                    <Typography variant="body1" fontWeight="bold">
                      ₹
                      {Number(watch(`items.${index}.unitPrice`) || 0) *
                        Number(watch(`items.${index}.quantity`) || 0)}
                    </Typography>
                  </Grid>

                  <Grid item display="flex" justifyContent="center">
                    <WSIcons.DeleteIcon
                      onClick={() => {
                        remove(index);
                        setTimeout(() => {
                          recalculateSubtotal();
                        }, 0);
                      }}
                      sx={{ color: "red", cursor: "pointer" }}
                    />
                  </Grid>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Typography variant="h6">Invoice Calculation :</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <WSTextField
                  label={"Invoice Amount"}
                  {...register("invoiceAmount")}
                  id="invoiceAmount"
                  name="invoiceAmount"
                  control={control}
                  rules={{ required: false }}
                  type={TextType.Currency}
                  currency={CurrencyType.Rupee}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <WSTextField
                  label={"Received"}
                  {...register("received")}
                  id="received"
                  name="received"
                  control={control}
                  rules={{ required: false }}
                  type={TextType.Currency}
                  currency={CurrencyType.Rupee}
                />
              </Grid>
              <Grid
                container
                sx={{ backgroundColor: "#e8f0f0", p: 2, mt: 2, ml: 2 }}
                spacing={2}
              >
                <Grid item xs={8}>
                  <WSView title="Balance" />
                </Grid>
                <Grid item xs={4} sx={{ textAlign: "right" }}>
                  <WSView
                    title={`₹ ${(
                      total - (Number(watch("received")) || 0)
                    ).toFixed(2)}`}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={!isFormValid()}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#d3d3d3",
                    color: isFormValid() ? "#fff" : "#555",
                  }}
                >
                  Create Your First Invoice
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography
            variant="h6"
            sx={{ mb: 2, fontWeight: "bold" }}
            textAlign={"center"}
          >
            1 cr vyaparis have created invoices on vyapar
          </Typography>
          <Paper elevation={0} sx={{ p: 3, minHeight: "100vh" }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography textAlign={"center"}>Tax Invoice</Typography>
              </Grid>
              <Grid item xs={12} mt={8}>
                <Typography textAlign={"center"}>TAX INVOICE</Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Typography variant="h6">Bill To :</Typography>
                <Typography variant="caption">
                  {" "}
                  {watch("customerName") || "Customer Name"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle1">Invoice Details:</Typography>
                <Typography variant="subtitle2">Invoice No:#1</Typography>
                <Typography variant="caption">
                  Date: {new Date().toLocaleDateString()}{" "}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ maxWidth: 900 }}
                >
                  <Table size="small" aria-label="header-only table">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Item Name
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          Quantity
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          Price/Unit
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {watch("items")?.map((item: any, index: number) => {
                        const qty = Number(item.quantity) || 0;
                        const price = Number(item.unitPrice) || 0;
                        const amount = qty * price;
                        return (
                          <TableRow key={index}>
                            <TableCell>{item.itemName || "—"}</TableCell>
                            <TableCell align="right">{qty}</TableCell>
                            <TableCell align="right">₹{price}</TableCell>
                            <TableCell align="right">₹{amount}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1">Amount in words -</Typography>
                <Typography variant="caption"> </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle1">Sub Total</Typography>
                <Typography variant="subtitle2">Total</Typography>
                <Typography variant="caption">Balance Due</Typography>
              </Grid>
              <Grid item xs={12} sm={2} textAlign="right">
                <Typography variant="subtitle1">
                  ₹ {subtotal.toFixed(2)}
                </Typography>
                <Typography variant="subtitle2">
                  ₹ {total.toFixed(2)}
                </Typography>
                <Typography variant="caption">
                  ₹ {(total - (Number(watch("received")) || 0)).toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default InvoicePage;
